// frontend/src/pages/yard/YardInventory.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { PlusIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import yardService from '../../services/yard.service';
import guardAuthService from '../../services/guardAuth.service';
import {
  YardInventoryItem,
  YardLocation,
  AddEntryRequest,
  UpdateEntryRequest,
  MarkOnYardRequest,
  LoadEmpty,
  ProLookupResponse,
} from '../../types/yard';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import SingleSelect from '../../components/SingleSelect';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatFullDateTime } from '../../utils/dateUtils';
import { isValidContainerNumber } from '../../utils/containerValidator';
import { describeApiError } from '../../utils/apiError';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

type ContextType = {
  selectedYard: number | null;
  setSelectedYard: (yard: number | null) => void;
};

type EquipmentType = 'CHASSIS' | 'VAN' | 'CONTAINER';

export default function YardInventory() {
  // Try to get context from GuardLayout, fallback if not available
  let selectedYard: number | null = null;
  let setSelectedYard: ((yard: number | null) => void) | null = null;

  try {
    const context = useOutletContext<ContextType>();
    selectedYard = context?.selectedYard ?? null;
    setSelectedYard = context?.setSelectedYard ?? null;
  } catch {
    // Not in GuardLayout, will use local state
  }

  const { showToast } = useToast();
  const { permissions } = usePermissions();

  // Shows a network-vs-server-aware error toast. Surfaces the backend's
  // reference id (matchable in system_log) and keeps connectivity / reference-id
  // failures on screen longer so they can be read and reported.
  const showApiErrorToast = (error: any, fallback: string) => {
    const info = describeApiError(error, fallback);
    showToast({
      message: info.message,
      type: 'error',
      duration: info.isNetworkError || info.errorId ? 12000 : undefined,
      copyPayload: info.errorId,
    });
  };

  // Managed-login (guard/yard/customer) accounts use guardAuthService;
  // MSAL employees don't. The Order Details drawer is wired only for
  // employees — guards/yard accounts won't have permission anyway.
  const isEmployee = !guardAuthService.isAuthenticated();

  // Local yard state for when not using GuardLayout
  const [localYard, setLocalYard] = useState<number | null>(null);
  const [yards, setYards] = useState<YardLocation[]>([]);
  const defaultYardAppliedRef = useRef(false);

  const currentYard = selectedYard ?? localYard;
  const updateYard = setSelectedYard ?? setLocalYard;

  // Auto-select the user's default yard once permissions load (employees only —
  // GuardLayout already wires its own default via guardAuthService).
  useEffect(() => {
    if (selectedYard !== null) return; // GuardLayout owns the selection
    if (defaultYardAppliedRef.current) return;
    if (localYard !== null) return; // user already picked something
    if (!permissions?.default_yard) return;
    setLocalYard(permissions.default_yard);
    defaultYardAppliedRef.current = true;
  }, [permissions?.default_yard, selectedYard, localYard]);

  // Inventory state
  const [inventory, setInventory] = useState<YardInventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [markingOnYard, setMarkingOnYard] = useState<number[]>([]);
  const [lastYardEvent, setLastYardEvent] = useState<string | null>(null);

  // Add Entry modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addEquipmentType, setAddEquipmentType] = useState<EquipmentType | null>(null);
  const [addProNumber, setAddProNumber] = useState('');
  const [addProLookup, setAddProLookup] = useState<ProLookupResponse | null>(null);
  const [addEquipno, setAddEquipno] = useState('');
  const [addChassisno, setAddChassisno] = useState('');
  const [addTrucknumber, setAddTrucknumber] = useState('');
  const [addCompanyname, setAddCompanyname] = useState('');
  const [addLoadempty, setAddLoadempty] = useState<LoadEmpty>('E');
  const [addComments, setAddComments] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Take New Inventory modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Edit Entry modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState<YardInventoryItem | null>(null);
  const [editEquipno, setEditEquipno] = useState('');
  const [editChassisno, setEditChassisno] = useState('');
  const [editTrucknumber, setEditTrucknumber] = useState('');
  const [editCompanyname, setEditCompanyname] = useState('');
  const [editLoadempty, setEditLoadempty] = useState<LoadEmpty>('E');
  const [editComments, setEditComments] = useState('');
  const [editProNumber, setEditProNumber] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editRemoving, setEditRemoving] = useState(false);

  // Header-drag for each of this page's three modals. Declared here at the
  // component top level (not inside renderAddModal) so hook order is stable.
  const addDrag = useModalDrag(showAddModal);
  const resetDrag = useModalDrag(showResetModal);
  const editDrag = useModalDrag(showEditModal);

  // Order Details Drawer (employees only — opened by clicking a PRO link)
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Load yard locations
  useEffect(() => {
    yardService.getYardLocations().then(setYards).catch(console.error);
  }, []);

  // Load inventory when yard changes. Pass { silent: true } for post-action
  // refreshes so the "Loading..." spinner doesn't flicker the UI.
  const loadInventory = useCallback(async (opts?: { silent?: boolean }) => {
    if (!currentYard) return;
    if (!opts?.silent) setLoading(true);
    try {
      const [data, lastEvent] = await Promise.all([
        yardService.getInventory(currentYard),
        yardService.getLastYardEvent(currentYard).catch(() => null),
      ]);
      setInventory(data);
      setLastYardEvent(lastEvent);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [currentYard]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Filter inventory by search term
  const filteredInventory = inventory
    .filter((item) => {
      const term = searchTerm.toLowerCase();
      return (
        item.equipno.toLowerCase().includes(term) ||
        item.chassisno.toLowerCase().includes(term) ||
        item.companyname.toLowerCase().includes(term) ||
        (item.customer_name?.toLowerCase() || '').includes(term) ||
        String(item.cmspronumber).includes(term)
      );
    })
    .sort((a, b) => {
      const eq = (a.equipno || '').localeCompare(b.equipno || '', undefined, { sensitivity: 'base' });
      if (eq !== 0) return eq;
      return (a.chassisno || '').localeCompare(b.chassisno || '', undefined, { sensitivity: 'base' });
    });

  // Split into two sections
  const pendingItems = filteredInventory.filter((item) => !item.on_yard);
  const onYardItems = filteredInventory.filter((item) => item.on_yard);

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // --- Action Handlers ---

  const handleMarkOnYard = async (item: YardInventoryItem) => {
    if (!currentYard) return;
    if (markingOnYard.includes(item.recno)) return; // Re-entrancy guard against double-clicks
    setMarkingOnYard((prev) => [...prev, item.recno]);

    const yardLoc = yards.find((y) => y.recno === currentYard)?.locname || '';
    const request: MarkOnYardRequest = {
      yardrecno: item.yardrecno,
      yardloc: yardLoc,
      equipno: item.equipno,
      cmspronumber: item.cmspronumber || undefined,
      companyname: item.companyname || undefined,
      trucknumber: item.trucknumber || undefined,
      chassisno: item.chassisno || undefined,
      loadempty: (item.loadempty === 'L' || item.loadempty === 'E' ? item.loadempty : undefined) as LoadEmpty | undefined,
      comments: item.comments || undefined,
    };
    try {
      await yardService.markOnYard(request);
      await loadInventory({ silent: true });
    } catch (error: any) {
      showApiErrorToast(error, 'Error marking on yard');
    } finally {
      setMarkingOnYard((prev) => prev.filter((id) => id !== item.recno));
    }
  };

  const handleMarkRemainingNotOnYard = async () => {
    if (pendingItems.length === 0) return;
    if (
      !window.confirm(
        `Mark ${pendingItems.length} remaining pending ${
          pendingItems.length === 1 ? 'item' : 'items'
        } as not on yard? This will remove them from yard inventory.`,
      )
    ) {
      return;
    }

    setBulkRemoving(true);
    const results = await Promise.allSettled(
      pendingItems.map((item) => yardService.deleteFromYard(item.recno)),
    );
    setBulkRemoving(false);

    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = results.length - failed;

    if (failed === 0) {
      showToast({
        message: `Removed ${succeeded} ${succeeded === 1 ? 'item' : 'items'} from yard`,
        type: 'success',
      });
    } else if (succeeded === 0) {
      showToast({ message: `Failed to remove ${failed} items`, type: 'error' });
    } else {
      showToast({
        message: `Removed ${succeeded}, failed ${failed}`,
        type: 'warning',
      });
    }
    loadInventory();
  };

  // --- Take New Inventory Handlers ---

  const openResetModal = () => {
    if (!currentYard) return;
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    if (resetting) return;
    setShowResetModal(false);
  };

  const handleResetInventory = async (removePending: boolean) => {
    if (!currentYard) return;
    setResetting(true);
    try {
      if (removePending && pendingItems.length > 0) {
        const results = await Promise.allSettled(
          pendingItems.map((item) => yardService.deleteFromYard(item.recno)),
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          showToast({
            message: `Failed to remove ${failed} pending ${failed === 1 ? 'item' : 'items'}`,
            type: 'warning',
          });
        }
      }

      const result = await yardService.resetInventory(currentYard);
      showToast({
        message: result.message || 'Inventory reset',
        type: 'success',
      });
      setShowResetModal(false);
    } catch (error: any) {
      showApiErrorToast(error, 'Error resetting inventory');
    } finally {
      setResetting(false);
      loadInventory();
    }
  };

  // --- Edit Entry Modal Handlers ---

  const openEditModal = (item: YardInventoryItem) => {
    setEditItem(item);
    setEditEquipno(item.equipno || '');
    setEditChassisno(item.chassisno || '');
    setEditTrucknumber(item.trucknumber || '');
    setEditCompanyname(item.customer_name || item.companyname || '');
    setEditLoadempty(item.loadempty === 'L' ? 'L' : item.loadempty === 'E' ? 'E' : 'N');
    setEditComments(item.comments || '');
    setEditProNumber(item.cmspronumber > 0 ? String(item.cmspronumber) : '');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;
    if (!editEquipno) {
      showToast({ message: 'Equipment number is required', type: 'error' });
      return;
    }
    if (editChassisno && editEquipno.trim().toUpperCase() === editChassisno.trim().toUpperCase()) {
      showToast({
        message: 'Equipment number cannot equal the chassis number. Clear the Container # for a bare chassis.',
        type: 'error',
      });
      return;
    }

    setEditLoading(true);
    try {
      const request: UpdateEntryRequest = {
        equipno: editEquipno,
        cmspronumber: editProNumber ? parseInt(editProNumber) : 0,
        companyname: editCompanyname,
        trucknumber: editTrucknumber,
        chassisno: editChassisno,
        loadempty: editLoadempty,
        comments: editComments,
      };

      const result = await yardService.updateEntry(editItem.recno, request);
      showToast({ message: result.message || 'Entry updated', type: 'success' });
      closeEditModal();
      loadInventory();
    } catch (error: any) {
      showApiErrorToast(error, 'Error updating entry');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRemoveFromYard = async () => {
    if (!editItem) return;
    if (!window.confirm(`Remove ${editItem.equipno || 'this entry'} from yard inventory?`)) {
      return;
    }
    setEditRemoving(true);
    try {
      await yardService.deleteFromYard(editItem.recno);
      showToast({ message: 'Removed from yard', type: 'success' });
      closeEditModal();
      loadInventory();
    } catch (error: any) {
      // A 404 means the row is already gone — most often because an earlier
      // attempt actually succeeded (the write commits before the response is
      // sent) but its response was lost in transit. Treat it as success so the
      // guard isn't stuck retrying a removal that already happened.
      if (error?.response?.status === 404) {
        showToast({ message: 'Already removed from yard', type: 'info' });
        closeEditModal();
        loadInventory();
        return;
      }
      showApiErrorToast(error, 'Error removing from yard');
    } finally {
      setEditRemoving(false);
    }
  };

  // --- Add Entry Modal Handlers ---

  const resetAddForm = () => {
    setAddEquipmentType(null);
    setAddProNumber('');
    setAddProLookup(null);
    setAddEquipno('');
    setAddChassisno('');
    setAddTrucknumber('');
    setAddCompanyname('');
    setAddLoadempty('E');
    setAddComments('');
  };

  const openAddModal = () => {
    resetAddForm();
    setShowAddModal(true);
  };

  // Quick-add from the search bar: opens the modal with equipment type
  // pre-selected and the typed value placed in the relevant field.
  const openAddModalWithPrefill = (type: EquipmentType) => {
    const value = searchTerm.trim().toUpperCase();
    resetAddForm();
    setAddEquipmentType(type);
    if (type === 'CHASSIS') {
      setAddChassisno(value);
    } else {
      setAddEquipno(value);
    }
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const handleAddProLookup = async () => {
    if (!addProNumber) return;
    try {
      const result = await yardService.lookupPro(parseInt(addProNumber));
      if (result) {
        setAddProLookup(result);
        if (result.equipno) setAddEquipno(result.equipno);
        if (result.chassisno) setAddChassisno(result.chassisno);
        if (result.customer_name) setAddCompanyname(result.customer_name);
      } else {
        showToast({ message: 'PRO not found', type: 'warning' });
        setAddProLookup(null);
      }
    } catch {
      showToast({ message: 'Error looking up PRO', type: 'error' });
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentYard) {
      showToast({ message: 'No yard selected', type: 'error' });
      return;
    }
    if (!addEquipmentType) {
      showToast({ message: 'Please select equipment type', type: 'error' });
      return;
    }
    if (!addEquipno && addEquipmentType !== 'CHASSIS') {
      showToast({ message: 'Please enter equipment number', type: 'error' });
      return;
    }
    if (!addChassisno && (addEquipmentType === 'CHASSIS' || addEquipmentType === 'CONTAINER')) {
      showToast({ message: 'Please enter chassis number', type: 'error' });
      return;
    }
    // A bare chassis must never be filed as a container. Require a valid ISO-6346
    // number when the entry is a CONTAINER so a chassis ID can't be entered in the
    // Container # field (the root cause of chassis showing as containers in reports).
    if (addEquipmentType === 'CONTAINER' && !isValidContainerNumber(addEquipno, { strict: false })) {
      showToast({
        message: 'Invalid container number — expected 4 letters followed by 7 digits. A bare chassis should be entered as CHASSIS, not CONTAINER.',
        type: 'error',
      });
      return;
    }

    setAddLoading(true);
    try {
      const request: AddEntryRequest = {
        yardrecno: currentYard,
        // Do NOT fall back to the chassis number: a bare chassis (CHASSIS type)
        // must save with equipno empty so it buckets as SSL Bare Chassis.
        equipno: addEquipno,
        equiptype: addEquipmentType,
        cmspronumber: addProNumber ? parseInt(addProNumber) : undefined,
        companyname: addCompanyname || undefined,
        trucknumber: addTrucknumber || undefined,
        chassisno: addChassisno || undefined,
        loadempty: addLoadempty,
        comments: addComments || undefined,
      };
      const result = await yardService.addEntry(request);
      showToast({ message: result.message || 'Entry added', type: 'success' });
      closeAddModal();
      loadInventory();
    } catch (error: any) {
      if (error.response?.status === 409) {
        showToast({
          message: error.response?.data?.detail || 'Duplicate entry - equipment already in yard',
          type: 'error',
        });
      } else {
        showApiErrorToast(error, 'Error adding entry');
      }
    } finally {
      setAddLoading(false);
    }
  };

  // --- Render Helpers ---

  const formatShortDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d.getTime()) ? iso : `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const getPerDiemUrgency = (dateStr: string | null) => {
    if (!dateStr) return '';
    const parsed = new Date(dateStr + 'T00:00:00');
    if (isNaN(parsed.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsed.setHours(0, 0, 0, 0);
    const diffDays = Math.round((parsed.getTime() - today.getTime()) / 86_400_000);
    if (diffDays <= 0) return 'bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50';
    if (diffDays === 1) return 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50';
    if (diffDays === 2) return 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50';
    return '';
  };

  const renderRow = (item: YardInventoryItem, isPending: boolean) => {
    const rowBgClass = getPerDiemUrgency(item.perdiem_start_date);
    return (
    <div
      key={item.recno}
      className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2 ${rowBgClass || 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'} border-b border-gray-100 dark:border-gray-800 text-sm`}
    >
      {/* Info - 3 stacked lines on mobile, single row on desktop */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 flex-1 min-w-0">
        {/* Edit button */}
        <button
          onClick={() => openEditModal(item)}
          className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors shrink-0"
        >
          Edit
        </button>

        {/* Equipment number */}
        <span className="font-semibold text-heading truncate">
          {item.equipno || '-'}
        </span>

        {/* Chassis */}
        <span className="text-muted truncate">
          {item.chassisno || '-'}
        </span>

        {/* Load status badge */}
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
            item.loadempty === 'L'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {item.loadempty === 'L' ? 'Loaded' : item.loadempty === 'E' ? 'Empty' : 'N/A'}
        </span>

        {/* Plugged In badge */}
        {item.pluggedin === 'Y' && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 whitespace-nowrap">
            Plugged In
          </span>
        )}

        {/* Mobile line break → line 2 */}
        <div className="basis-full h-0 sm:hidden" aria-hidden="true" />

        {/* Company / Customer */}
        <span className="text-gray-600 dark:text-gray-300 truncate sm:flex-1">
          {item.customer_name || item.companyname || 'Unknown'}
        </span>

        {/* PRO — clickable for MSAL employees, plain text for guard/yard accounts */}
        {item.cmspronumber > 0 ? (
          isEmployee ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openOrderDrawer(item.cmspronumber);
              }}
              className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium whitespace-nowrap"
            >
              PRO: {item.cmspronumber}
            </button>
          ) : (
            <span className="text-muted text-xs whitespace-nowrap">
              PRO: {item.cmspronumber}
            </span>
          )
        ) : (
          <span className="text-muted text-xs whitespace-nowrap">-</span>
        )}

        {/* Per Diem date + accrued amount */}
        {item.perdiem_start_date && (
          <span className="text-xs whitespace-nowrap text-label">
            PD {formatShortDate(item.perdiem_start_date)}
            {item.perdiem_amount != null && item.perdiem_amount > 0 && (
              <span className="ml-1 font-semibold text-red-700 dark:text-red-400">
                ${item.perdiem_amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            )}
          </span>
        )}

        {/* Mobile line break → line 3 */}
        <div className="basis-full h-0 sm:hidden" aria-hidden="true" />

        {/* Check-in datetime */}
        <span className="text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
          In: {formatDateTime(item.indatetime)}
        </span>

        {/* On-yard datetime (recorded section only) */}
        {!isPending && item.on_yard_datetime && (
          <span className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
            On: {formatDateTime(item.on_yard_datetime)}
          </span>
        )}
      </div>

      {/* Actions - full width on mobile, auto on desktop */}
      {isPending && (
        <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
          <button
            onClick={() => handleMarkOnYard(item)}
            disabled={markingOnYard.includes(item.recno)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 sm:py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            title="Mark as on yard"
          >
            <CheckCircleIcon className="w-7 h-7" />
            {markingOnYard.includes(item.recno) ? 'Marking...' : 'On Yard'}
          </button>
        </div>
      )}
    </div>
    );
  };

  const renderSection = (
    title: string,
    items: YardInventoryItem[],
    isPending: boolean,
  ) => (
    <div className="mb-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-t border border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h2>
        <span className="text-xs text-muted bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
          {items.length}
        </span>
      </div>
      {/* Rows */}
      <div className="border border-t-0 border-gray-200 dark:border-gray-700 rounded-b">
        {items.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500 italic">
            No items
          </div>
        ) : (
          items.map((item) => renderRow(item, isPending))
        )}
      </div>
    </div>
  );

  // --- Add Entry Modal ---

  const renderAddModal = () => {
    if (!showAddModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-scrim/50"
          onClick={closeAddModal}
        />
        {/* Modal */}
        <div
          ref={addDrag.panelRef}
          style={addDrag.panelStyle}
          className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        >
          <ModalHeader
            title="Add Yard Entry"
            onClose={closeAddModal}
            dragHandleProps={addDrag.dragHandleProps}
          />

          {/* Body */}
          <form onSubmit={handleAddSubmit} className="px-5 py-4 space-y-4">
            {/* Equipment Type Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Equipment Type
              </label>
              <div className="flex gap-2">
                {(
                  [
                    { type: 'CHASSIS' as EquipmentType, label: 'Chassis' },
                    { type: 'VAN' as EquipmentType, label: 'Van' },
                    { type: 'CONTAINER' as EquipmentType, label: 'Container' },
                  ] as const
                ).map(({ type, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAddEquipmentType(type)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded border transition-colors ${
                      addEquipmentType === type
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-panel text-gray-700 border-gray-300 hover:bg-highlight dark:text-gray-200 dark:border-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* PRO Number with Lookup */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                PRO Number
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={addProNumber}
                  onChange={(e) => setAddProNumber(e.target.value)}
                  className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                  placeholder="Enter PRO number"
                />
                <button
                  type="button"
                  onClick={handleAddProLookup}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Lookup
                </button>
              </div>
              {addProLookup && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                  <div>
                    <strong>Customer:</strong> {addProLookup.customer_name || 'N/A'}
                  </div>
                  <div>
                    <strong>Route:</strong> {addProLookup.origin || 'N/A'} &rarr;{' '}
                    {addProLookup.destination || 'N/A'}
                  </div>
                </div>
              )}
            </div>

            {/* Container # (CONTAINER type) */}
            {addEquipmentType === 'CONTAINER' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Container #
                </label>
                <input
                  type="text"
                  value={addEquipno}
                  onChange={(e) => setAddEquipno(e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                  placeholder="e.g., MSCU1234567"
                />
              </div>
            )}

            {/* Chassis # (CHASSIS or CONTAINER type) */}
            {(addEquipmentType === 'CHASSIS' || addEquipmentType === 'CONTAINER') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Chassis #
                </label>
                <input
                  type="text"
                  value={addChassisno}
                  onChange={(e) => setAddChassisno(e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                  placeholder="e.g., CHAZ12345"
                />
              </div>
            )}

            {/* Van/Trailer # (VAN type) */}
            {addEquipmentType === 'VAN' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Van/Trailer #
                </label>
                <input
                  type="text"
                  value={addEquipno}
                  onChange={(e) => setAddEquipno(e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                  placeholder="e.g., VAN12345"
                />
              </div>
            )}

            {/* Truck Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Truck #
              </label>
              <input
                type="text"
                value={addTrucknumber}
                onChange={(e) => setAddTrucknumber(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                placeholder="Truck number"
              />
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={addCompanyname}
                onChange={(e) => setAddCompanyname(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                placeholder="Company name"
              />
            </div>

            {/* Load/Empty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Load Status
              </label>
              <div className="flex gap-4">
                <label className="flex items-center text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={addLoadempty === 'L'}
                    onChange={() => setAddLoadempty('L')}
                    className="mr-2"
                  />
                  Loaded
                </label>
                <label className="flex items-center text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={addLoadempty === 'E'}
                    onChange={() => setAddLoadempty('E')}
                    className="mr-2"
                  />
                  Empty
                </label>
                <label className="flex items-center text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={addLoadempty === 'N'}
                    onChange={() => setAddLoadempty('N')}
                    className="mr-2"
                  />
                  N/A
                </label>
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Comments
              </label>
              <textarea
                value={addComments}
                onChange={(e) => setAddComments(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-3 py-2 text-sm"
                rows={2}
                placeholder="Optional comments"
              />
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeAddModal}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addLoading || !addEquipmentType}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {addLoading ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // --- Main Render ---

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold dark:text-white">Update Yard Inventory</h1>
        <div className="flex items-center gap-2">
          {currentYard && (
            <>
              <button
                onClick={openResetModal}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Take New Inventory
              </button>
              <button
                onClick={openAddModal}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Entry
              </button>
              <button
                onClick={() => loadInventory()}
                className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1.5"
              >
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Yard Selector (for employee view) */}
      {!selectedYard && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 dark:text-white">Select Yard</label>
          <SingleSelect
            options={yards.map((yard) => ({ value: String(yard.recno), label: yard.locname }))}
            value={currentYard != null ? String(currentYard) : ''}
            onChange={(v) => updateYard(v ? Number(v) : null)}
            placeholder="-- Select Yard --"
            enableSearch
            searchPlaceholder="Search yards…"
            className="w-full"
          />
        </div>
      )}

      {currentYard && (
        <>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search equipment, chassis, company, PRO..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
            />
            {(() => {
              const trimmed = searchTerm.trim();
              if (!trimmed) return null;
              const upper = trimmed.toUpperCase();
              return (
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  {(
                    [
                      { type: 'CONTAINER' as EquipmentType, label: 'Container' },
                      { type: 'CHASSIS' as EquipmentType, label: 'Chassis' },
                      { type: 'VAN' as EquipmentType, label: 'Van' },
                    ] as const
                  ).map(({ type, label }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => openAddModalWithPrefill(type)}
                      className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 rounded transition-colors"
                    >
                      Add{' '}
                      <span className="text-yellow-300">{upper}</span>{' '}
                      as New {label} #
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Inventory Sections */}
          {loading ? (
            <div className="text-center py-8 text-muted">Loading...</div>
          ) : (
            <>
              {pendingItems.length > 0 && lastYardEvent && (
                <div className="mb-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-label">
                  Inventory Last Performed:{' '}
                  <span className="font-semibold">{formatFullDateTime(lastYardEvent)}</span>
                </div>
              )}
              {renderSection('Pending Verification', pendingItems, true)}
              {pendingItems.length > 0 && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                  <div className="text-sm text-label">
                    <span className="font-semibold">{pendingItems.length}</span>{' '}
                    {pendingItems.length === 1 ? 'item' : 'items'} remaining in pending verification
                  </div>
                  <button
                    onClick={handleMarkRemainingNotOnYard}
                    disabled={bulkRemoving}
                    className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {bulkRemoving ? 'Removing...' : 'Mark Remaining Not On Yard'}
                  </button>
                </div>
              )}
              {renderSection('On Yard', onYardItems, false)}
            </>
          )}
        </>
      )}

      {/* Take New Inventory Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50" onClick={closeResetModal}>
          <div
            ref={resetDrag.panelRef}
            style={resetDrag.panelStyle}
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title="Take New Inventory"
              onClose={resetting ? undefined : closeResetModal}
              dragHandleProps={resetDrag.dragHandleProps}
            />
            <div className="px-6 py-5 space-y-3 text-sm text-label">
              {pendingItems.length > 0 ? (
                <>
                  <p>
                    There {pendingItems.length === 1 ? 'is' : 'are'}{' '}
                    <span className="font-semibold">{pendingItems.length}</span>{' '}
                    {pendingItems.length === 1 ? 'item' : 'items'} still in Pending Verification that
                    {pendingItems.length === 1 ? ' has' : ' have'} not been verified.
                  </p>
                  <p>Would you like to remove them from the yard before starting the new inventory?</p>
                  <p className="text-xs text-muted">
                    Either way, all on-yard items will move back to Pending Verification so the yard
                    can be re-verified.
                  </p>
                </>
              ) : (
                <p>
                  Start a new inventory? All on-yard items will move back to Pending Verification
                  so the yard can be re-verified.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t dark:border-gray-700 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeResetModal}
                disabled={resetting}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 rounded disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleResetInventory(false)}
                disabled={resetting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {resetting
                  ? 'Working...'
                  : pendingItems.length > 0
                    ? 'Keep & Reset'
                    : 'Start New Inventory'}
              </button>
              {pendingItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleResetInventory(true)}
                  disabled={resetting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {resetting ? 'Working...' : 'Remove & Reset'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {renderAddModal()}

      {/* Edit Entry Modal */}
      {showEditModal && editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50" onClick={closeEditModal}>
          <div
            ref={editDrag.panelRef}
            style={editDrag.panelStyle}
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={`Edit Entry — ${editItem.equipno}`}
              onClose={closeEditModal}
              dragHandleProps={editDrag.dragHandleProps}
            />

            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {/* Equipment Number */}
              <div>
                <label className="block text-sm font-medium mb-1">Equipment #</label>
                <input
                  type="text"
                  value={editEquipno}
                  onChange={(e) => setEditEquipno(e.target.value.toUpperCase())}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* Chassis Number */}
              <div>
                <label className="block text-sm font-medium mb-1">Chassis #</label>
                <input
                  type="text"
                  value={editChassisno}
                  onChange={(e) => setEditChassisno(e.target.value.toUpperCase())}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* PRO Number */}
              <div>
                <label className="block text-sm font-medium mb-1">PRO Number</label>
                <input
                  type="number"
                  value={editProNumber}
                  onChange={(e) => setEditProNumber(e.target.value)}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="PRO number"
                />
              </div>

              {/* Truck Number */}
              <div>
                <label className="block text-sm font-medium mb-1">Truck #</label>
                <input
                  type="text"
                  value={editTrucknumber}
                  onChange={(e) => setEditTrucknumber(e.target.value)}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Company</label>
                <input
                  type="text"
                  value={editCompanyname}
                  onChange={(e) => setEditCompanyname(e.target.value)}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* Load/Empty */}
              <div>
                <label className="block text-sm font-medium mb-2">Load Status</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={editLoadempty === 'L'}
                      onChange={() => setEditLoadempty('L')}
                      className="mr-2"
                    />
                    Loaded
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={editLoadempty === 'E'}
                      onChange={() => setEditLoadempty('E')}
                      className="mr-2"
                    />
                    Empty
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={editLoadempty === 'N'}
                      onChange={() => setEditLoadempty('N')}
                      className="mr-2"
                    />
                    N/A
                  </label>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium mb-1">Comments</label>
                <textarea
                  value={editComments}
                  onChange={(e) => setEditComments(e.target.value)}
                  className="w-full border rounded px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleRemoveFromYard}
                  disabled={editRemoving || editLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {editRemoving ? 'Removing...' : 'Remove From Yard'}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading || editRemoving}
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Details Drawer (employees only — opened from PRO link) */}
      {isEmployee && (
        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen={isOrderDrawerOpen && selectedPro !== null}
          onClose={closeOrderDrawer}
        />
      )}
    </div>
  );
}
